import { useEffect, useId, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { hasCapability } from "@/lib/brokerAuth";
import {
  getAccount,
  type AccountDetail,
  type AccountDocument,
} from "@/lib/brokerAccounts";
import {
  CAPACITY_REQUEST_DOCUMENT_TYPE,
  CAPACITY_REQUEST_FILED,
  PROJECT_NOT_HOLDING_CAPACITY,
  capacityProjectLabel,
  listCapacityProjects,
  readCapacityRequest,
  submitCapacityRequest,
  type CapacityProject,
  type CapacityRequestKind,
} from "@/lib/capacityRequest";
import { apiErrorCode } from "@/lib/api";
import { formatBondDate } from "@/lib/bondValueDisplay";
import { messageFor } from "@/lib/errors";
import { useDocumentTypes } from "@/lib/useDocumentTypes";
import { AccountDocumentSlot } from "@/components/AccountDocumentSlot";
import { PageHeader } from "@/components/PageHeader";
import { SelectField } from "@/components/SelectField";
import { buttonClass } from "@/components/buttonStyles";

// BRK-S329 — "Request more capacity" (`/surety/accounts/:id/capacity-request`),
// consuming PMS-S723.
//
// Two routes (plan rule 16): a WIP report on a bonded job — the project picked
// from the orders HOLDING capacity on the account, one per order (projects
// consume capacity, not bonds — Aaron, 2026-10-14) — or a special request. Each uploads its
// documents to the ACCOUNT (the route PMS-S722 adds), takes required notes, and
// files the request to the underwriter's decision page.
//
// NOTHING HERE IS OPTIMISTIC (NN #7): a document is listed only from the
// upload's 201, and "Your request is with your underwriter" renders only from
// the POST's 2xx. A refusal (one already open, say) is PMS's sentence, inline,
// and every value entered stays.

const ROUTES: { value: CapacityRequestKind; label: string; detail: string }[] =
  [
    {
      value: "capacity_wip",
      label: "WIP report on a bonded job",
      detail:
        "Show progress on a project we bonded, so the work completed frees capacity.",
    },
    {
      value: "capacity_special",
      label: "Special request",
      detail:
        "Anything else — tell us what you need and upload what supports it.",
    },
  ];

export function CapacityRequestPage() {
  const { id = "" } = useParams();
  const { identity, authedRequest } = useAuth();
  const canEdit = hasCapability(identity, "account:edit");
  const [account, setAccount] = useState<AccountDetail | null>(null);
  const [projects, setProjects] = useState<CapacityProject[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!canEdit) return;
    let cancelled = false;
    void (async () => {
      try {
        const [fresh, holding] = await Promise.all([
          getAccount(authedRequest, id),
          // A failed read leaves the special request open rather than
          // blocking the screen; the WIP route then says nothing is held.
          listCapacityProjects(authedRequest, id).catch(
            (): CapacityProject[] => [],
          ),
        ]);
        if (cancelled) return;
        setAccount(fresh);
        setProjects(holding);
      } catch (err) {
        if (!cancelled) setLoadError(messageFor(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authedRequest, id, canEdit]);

  const back = (
    <Link
      to={`/surety/accounts/${encodeURIComponent(id)}`}
      className="text-sm text-brk-accent hover:underline"
    >
      ← Back to the account
    </Link>
  );

  let body: ReactNode;
  if (!canEdit) {
    body = (
      <p role="alert" className="text-sm text-brk-danger">
        You don’t have access to request capacity for this account.
      </p>
    );
  } else if (loadError) {
    body = (
      <p role="alert" className="text-sm text-brk-danger">
        {loadError}
      </p>
    );
  } else if (!account || projects === null) {
    body = (
      <p role="status" className="text-sm text-brk-dim">
        Loading…
      </p>
    );
  } else {
    const open = readCapacityRequest(account.capacityRequest);
    body =
      open?.status === "pending" ? (
        <Panel id="capacity-open" title="Capacity request under review">
          <p className="text-sm text-brk-text">
            A request for this insured is already with your underwriter
            {open.submittedAt
              ? ` (submitted ${formatBondDate(open.submittedAt)})`
              : ""}
            . You’ll be notified when it’s decided.
          </p>
        </Panel>
      ) : (
        <CapacityRequestForm accountId={account.id} projects={projects} />
      );
  }

  return (
    <>
      <PageHeader
        title="Request more capacity"
        meta={account ? account.legalName : undefined}
      />
      <section className="flex min-w-0 flex-col gap-6">
        {body}
        {back}
      </section>
    </>
  );
}

function CapacityRequestForm({
  accountId,
  projects,
}: {
  accountId: string;
  projects: CapacityProject[];
}) {
  const { authedRequest } = useAuth();
  const {
    rules,
    labelFor,
    error: typesError,
  } = useDocumentTypes(authedRequest);
  const notesId = useId();
  const reasonsId = useId();
  const [kind, setKind] = useState<CapacityRequestKind | null>(null);
  const [project, setProject] = useState("");
  const [notes, setNotes] = useState("");
  const [documents, setDocuments] = useState<AccountDocument[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // PMS's PROJECT_NOT_HOLDING_CAPACITY, on the select it is about.
  const [projectError, setProjectError] = useState<string | null>(null);
  const [filed, setFiled] = useState(false);

  if (filed) {
    return (
      <Panel id="capacity-filed" title="Request sent">
        <p role="status" className="text-sm text-brk-text">
          {CAPACITY_REQUEST_FILED}
        </p>
      </Panel>
    );
  }

  const reasons: string[] = [];
  if (kind === null) reasons.push("Choose the kind of request.");
  if (kind === "capacity_wip" && project === "") {
    reasons.push("Choose the bonded project.");
  }
  if (documents.length === 0) reasons.push("Upload at least one document.");
  if (notes.trim() === "") reasons.push("Add notes for your underwriter.");

  async function submit() {
    if (reasons.length > 0 || kind === null) return;
    setBusy(true);
    setError(null);
    setProjectError(null);
    try {
      await submitCapacityRequest(authedRequest, accountId, {
        kind,
        ...(kind === "capacity_wip" ? { bondSubmissionId: project } : {}),
        notes: notes.trim(),
        documentIds: documents.map((d) => d.id),
      });
      setFiled(true);
    } catch (err) {
      if (apiErrorCode(err) === PROJECT_NOT_HOLDING_CAPACITY) {
        setProjectError(messageFor(err));
      } else {
        setError(messageFor(err));
      }
    } finally {
      setBusy(false);
    }
  }

  const docType = kind ? CAPACITY_REQUEST_DOCUMENT_TYPE[kind] : null;

  return (
    <>
      <Panel id="capacity-route" title="What kind of request?">
        <fieldset className="flex flex-col gap-2">
          <legend className="sr-only">Kind of request</legend>
          {ROUTES.map((option) => (
            <label
              key={option.value}
              className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${
                kind === option.value
                  ? "border-brk-accent bg-brk-accent/5"
                  : "border-brk-border hover:bg-brk-bg"
              }`}
            >
              <input
                type="radio"
                name="capacity-route"
                className="mt-1"
                value={option.value}
                checked={kind === option.value}
                onChange={() => {
                  // Documents already on the account stay there; the request
                  // names only the ones uploaded for its own route.
                  if (kind !== option.value) setDocuments([]);
                  setKind(option.value);
                }}
              />
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="text-sm font-medium text-brk-text">
                  {option.label}
                </span>
                <span className="text-xs text-brk-dim">{option.detail}</span>
              </span>
            </label>
          ))}
        </fieldset>
        {kind === "capacity_wip" &&
          (projects.length > 0 ? (
            <div className="w-full md:w-96">
              <SelectField
                label="Bonded project"
                placeholder="Select…"
                required
                value={project}
                error={projectError ?? undefined}
                onChange={(e) => {
                  setProject(e.target.value);
                  setProjectError(null);
                }}
                options={projects.map((p) => ({
                  value: p.orderId,
                  label: capacityProjectLabel(p),
                }))}
              />
            </div>
          ) : (
            <p className="text-sm text-brk-dim">
              No project on this account is holding capacity right now. Choose
              “Special request” instead.
            </p>
          ))}
      </Panel>

      {kind !== null && docType !== null && (
        <Panel id="capacity-documents" title="Documents">
          {typesError && (
            <p role="alert" className="text-sm text-brk-danger">
              {typesError}
            </p>
          )}
          {rules ? (
            <AccountDocumentSlot
              key={docType}
              accountId={accountId}
              inputLabel="Upload capacity documents"
              types={[{ key: docType, label: labelFor(docType) }]}
              rules={rules}
              documents={documents}
              onUploaded={(doc) => setDocuments((prev) => [...prev, doc])}
              onRemoved={(removed) =>
                setDocuments((prev) => prev.filter((d) => d.id !== removed))
              }
            />
          ) : (
            !typesError && (
              <p role="status" className="text-sm text-brk-dim">
                Loading…
              </p>
            )
          )}
        </Panel>
      )}

      {kind !== null && (
        <Panel id="capacity-notes" title="Notes">
          <label htmlFor={notesId} className="text-sm text-brk-dim">
            What should your underwriter know? (required)
          </label>
          <textarea
            id={notesId}
            value={notes}
            rows={4}
            required
            onChange={(e) => setNotes(e.target.value)}
            className="w-full rounded-md border border-brk-border bg-brk-panel px-3 py-2 text-brk-text outline-none focus:border-brk-accent focus:ring-2 focus:ring-brk-accent/30"
          />
        </Panel>
      )}

      <div className="flex flex-col gap-3">
        {error && (
          <p role="alert" className="text-sm text-brk-danger">
            {error}
          </p>
        )}
        {reasons.length > 0 && (
          <ul
            id={reasonsId}
            className="flex flex-col gap-1 text-sm text-brk-dim"
          >
            {reasons.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
        )}
        <button
          type="button"
          onClick={() => void submit()}
          disabled={busy || reasons.length > 0}
          aria-describedby={reasons.length > 0 ? reasonsId : undefined}
          className={`${buttonClass()} self-start disabled:cursor-not-allowed disabled:opacity-60`}
        >
          {busy ? "Sending…" : "Send to your underwriter"}
        </button>
      </div>
    </>
  );
}

function Panel({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <section
      aria-labelledby={id}
      className="flex min-w-0 flex-col gap-4 rounded-xl border border-brk-border bg-brk-panel p-4 sm:p-6"
    >
      <h2 id={id} className="text-heading text-brk-text">
        {title}
      </h2>
      {children}
    </section>
  );
}
